import { Form } from "antd"
import { Legend, notification, Number, TextInput } from "components/common"
import { useCallback, useEffect, useMemo } from "react"
import { customValidatorForAddPayment, customValidatorForVASAddPayment, fieldMapper } from "../../../../utils"
import { t } from "i18next"
import { useSelector } from "react-redux"
import { numberProps } from "features/leads/components/addSalesModal/utils/constants"
import { useAddSalesContext } from "features/leads/components/addSalesModal/addSalesContext/addSalesContext"
import { debounce } from "lodash"
import IBANInfo from "features/shared/components/ibanInfo/ibanInfo"
import getPrivilege from "utils/privileges/privileges"
import PaymentProofImageUpload from "../paymentProofImageUpload"
import { useGetDataFromImageMutation, useLazyGetAccountantsQuery } from "features/leads/api"
import SuggestedAmountTags from "./components/SuggestedAmountTags"
import RACheckbox from "./components/RACheckbox"
import AccountantSection from "./components/AccountantSection"
import ExtraFieldsSection from "./components/ExtraFieldsSection"
import PaymentDetailsCollapse from "./components/PaymentDetailsCollapse"
import { formatAmountSuffix, getNextTaskKey, getPaymentSuffixMessage } from "./utils/paymentUtils"

const PaymentSection = ({ rules, clientIbanRef }) => {
  const {
    addSalesApis,
    form,
    stateManager,
    formWatchers,
    minTokenDetails,
    isEscrowSelfFinance,
    lastPaymentStatus,
    lastPaymentType,
    client,
    lead,
    showNextPayment,
    vasEnabled,
    vasFees,
  } = useAddSalesContext()

  const { project, unit, cdpCommitment, amount } = formWatchers
  const {
    leadSalesInfo,
    taskTypes,
    saleEvent,
    getBankName,
    validateReference,
    getPaymentMode,
    getContractExpiry,
    viban,
    fetchingVIBAN,
    clientIban,
    paymentModesMap,
  } = addSalesApis

  const { setFieldsValue, validateFields } = form

  // Form watchers
  const modeOfPayment = Form.useWatch("modeOfPayment", form)
  const enableRA = Form.useWatch("enableRA", form)
  const paymentProof = Form.useWatch("paymentProof", form)
  const depositSlip = Form.useWatch("bank_deposit_slip", form)

  const [getDataFromImage, { data: ocrData, isFetching, isLoading }] = useGetDataFromImageMutation()
  const [getAccountant, { data: accountantData = [], isLoading: isLoadingAccountant }] = useLazyGetAccountantsQuery()

  const userPlatformKey = useSelector(state => state.app.user?.info?.AgencyConfiguration?.Platform?.platformKey)
  const projectId = project?.id || stateManager?.project?.projectId || leadSalesInfo?.projectId
  const isRaEnabled =
    project?.isRAFlowEnabled || stateManager?.project?.isRAFlowEnabled || leadSalesInfo?.isRAFlowEnabled
  const ocrEnabled =
    stateManager?.project?.paymentProof || project?.paymentProof || leadSalesInfo?.project?.paymentProof

  // Handle payment proof changes - get payment mode
  useEffect(() => {
    if (paymentProof) {
      const id = paymentModesMap?.[paymentProof?.attachmentType]?.meta?.id
      if (id) {
        getPaymentMode(id, true)
      }
    }
  }, [paymentProof])

  // Handle payment proof type changes - only runs when attachmentType changes
  useEffect(() => {
    if (!paymentProof?.imageData?.paymentModeKey || !ocrData?.imageData || !modeOfPayment) return

    const { imageData } = ocrData
    const meta = paymentModesMap?.[modeOfPayment]?.meta

    if (modeOfPayment === "cheque") {
      getBankName({}, true)
      fieldMapper(imageData, meta?.cheque, form)

      if (imageData?.chequeNumber) {
        validateReference({
          referenceNumber: imageData?.chequeNumber,
          bankKey: imageData?.Bank?.key,
          paymentModeKey: modeOfPayment,
        })
          .unwrap()
          .then(() => validateFields(["referenceNumber"]))
          .catch(e => notification.error({ message: e.message }))
      }
    } else if (modeOfPayment === "online_payment") {
      getBankName({}, true)
      fieldMapper(imageData, meta?.onlinePayment, form)
      setFieldsValue({ paymentType: imageData?.onlinePaymentType })
    } else if (modeOfPayment === "card_payment") {
      fieldMapper(imageData, meta?.cardPayment, form)
    } else if (modeOfPayment === "cash") {
      getBankName({}, true)
      fieldMapper(imageData, meta?.depositSlip, form)
    } else if (modeOfPayment === "bank_order") {
      getBankName({}, true)
    }
  }, [paymentProof?.imageData, ocrData, modeOfPayment])

  const debouncedGetContractExpiry = useCallback(
    debounce(params => {
      getContractExpiry(params).unwrap().catch(console.error)
    }, 500),
    []
  )

  const updateNextTask = useCallback(
    (rawValue, tokenDetails) => {
      const key = getNextTaskKey({
        value: +rawValue,
        leadSalesInfo,
        dealPrice: stateManager?.dealPrice,
        cdpCommitment,
        project,
        saleEvent,
        tokenDetails,
        vasEnabled,
        vasFees,
      })
      form.setFieldValue("nextTask", key ? taskTypes?.nextStatuses?.find(item => item.key === key)?.id : null)
    },
    [stateManager?.dealPrice, leadSalesInfo, project, taskTypes, cdpCommitment, vasEnabled, vasFees, saleEvent]
  )

  const onChangeAmount = useCallback(
    (amount, tokenDetails) => {
      form.setFieldValue("deadLine", null)
      updateNextTask(amount, tokenDetails)
      form
        .validateFields(["amount"])
        .then(() => {
          debouncedGetContractExpiry({
            listingId: unit?.listingId ?? leadSalesInfo?.listingId,
            params: {
              tokenAmount: amount,
              dealPrice: stateManager?.dealPrice,
              contractListingId: leadSalesInfo?.contracts?.[0]?.contractListingId,
            },
          })
        })
        .catch(({ errorFields }) => errorFields?.forEach(item => item?.errors?.forEach(console.log)))
    },
    [stateManager, leadSalesInfo, unit, updateNextTask, debouncedGetContractExpiry]
  )

  const handleSuggestedAmountClick = element => {
    form.setFieldsValue({ amount: element.props.value })
    onChangeAmount(element.props.value, minTokenDetails)
  }

  const suffixHandler = useCallback(() => {
    const message = getPaymentSuffixMessage({
      amount,
      dealPrice: stateManager?.dealPrice,
      vasEnabled,
      leadSalesInfo,
      vasFees,
    })
    return formatAmountSuffix(message, amount, numberProps)
  }, [amount, stateManager?.dealPrice, leadSalesInfo, vasEnabled, vasFees])

  const suggestedAmounts = useMemo(() => {
    const calculateValue = (value, unit, fallback) =>
      value !== undefined ? (unit === "percentage" ? (value / 100) * stateManager?.dealPrice : value) : fallback

    const token = calculateValue(
      leadSalesInfo?.token,
      leadSalesInfo?.tokenUnit,
      calculateValue(minTokenDetails.minToken, minTokenDetails.minTokenUnit, 0)
    )
    const pdp = leadSalesInfo?.remainParDowPayment ?? project?.pdpPercentage * stateManager?.dealPrice
    const cdp =
      leadSalesInfo?.remainComDowPayment ??
      ((cdpCommitment ?? project?.ProjectFinancial?.completeDownPayment) / 100) * stateManager?.dealPrice

    const components = []
    const addComponent = (value, suffix, key) =>
      components.push(<Number value={Math.round(value)} suffix={suffix} key={key} />)

    const firstSales = leadSalesInfo?.isFirstSalesTask
    const vasRemaining = leadSalesInfo?.isVasRemaining
    const rejectedToken = lastPaymentStatus === "rejected" && lastPaymentType === "token"

    if (vasEnabled && firstSales) {
      addComponent(vasFees, "(VAS Fee)", "vas")
    } else if (vasEnabled && vasRemaining) {
      addComponent(leadSalesInfo?.vasRemianingAmount, "(VAS Remaining Fee)", "vasRemain")
    } else if (!leadSalesInfo || firstSales || rejectedToken) {
      addComponent(token, "(Token)", "token")
      if (!isEscrowSelfFinance) {
        addComponent(pdp, "(PDP)", "pdp")
        addComponent(cdp, "(CDP)", "cdp")
      }
    } else {
      if (pdp > 0) addComponent(pdp, "(PDP)", "pdp")
      if (cdp > 0) addComponent(cdp, "(CDP)", "cdp")
    }

    return components
  }, [
    project,
    leadSalesInfo,
    stateManager?.dealPrice,
    cdpCommitment,
    minTokenDetails,
    isEscrowSelfFinance,
    vasEnabled,
    lastPaymentStatus,
    lastPaymentType,
    vasFees,
  ])

  const amountValidationRules = useMemo(
    () =>
      rules.concat({
        validator: async (_, value) => {
          if (vasEnabled && leadSalesInfo?.isFirstSalesTask) {
            return customValidatorForVASAddPayment(value, leadSalesInfo, stateManager, vasEnabled, vasFees)
          }
          if (vasEnabled && leadSalesInfo?.isVasRemaining) {
            return customValidatorForVASAddPayment(
              value,
              leadSalesInfo,
              stateManager,
              vasEnabled,
              leadSalesInfo?.vasRemianingAmount
            )
          }
          return customValidatorForAddPayment(
            value,
            saleEvent,
            minTokenDetails,
            leadSalesInfo,
            stateManager,
            isEscrowSelfFinance
          )
        },
      }),
    [rules, vasEnabled, leadSalesInfo, stateManager, vasFees, saleEvent, minTokenDetails, isEscrowSelfFinance]
  )

  const handlePaymentProofUpload = data => {
    if (data?.imageData && ocrEnabled) {
      const extraField = paymentModesMap?.[data?.imageData?.paymentModeKey]?.extraFields
      if (extraField?.["bank_deposit_slip"]) {
        notification.info({
          message: `${t("Payment of type")} ${data?.imageData?.paymentMode} ${t(
            "detected. Please upload deposit slip as well."
          )}`,
        })
      }
      getDataFromImage({
        attachmentTypeId: data?.imageData?.attachmentTypeId,
        file: data?.url,
        name: data?.name,
        projectId: data?.projectId,
        detectPaymentMode: false,
      })
        .unwrap()
        .catch(error => notification?.error({ message: error?.message }))

      form.setFieldsValue({
        paymentProof: { ...data, attachmentTypeId: data?.imageData?.attachmentTypeId },
        modeOfPayment: data?.imageData?.paymentModeKey,
      })
    } else {
      form.setFieldsValue({
        paymentProof: { ...data, attachmentTypeId: data?.imageData?.attachmentTypeId },
        modeOfPayment: null,
      })
    }
  }

  // Early return if section shouldn't render
  if (leadSalesInfo?.verifiedWon || (!leadSalesInfo?.unitPrice && (!unit || !project))) return null

  const showIBANInfo = isEscrowSelfFinance && !leadSalesInfo?.isFirstSalesTask && lastPaymentStatus !== "rejected"

  return (
    <>
      {!!showIBANInfo && (
        <IBANInfo
          clientId={client?.id}
          clientIbanRef={clientIbanRef}
          vIBAN={viban?.data?.virtualIban}
          contractId={leadSalesInfo?.contracts?.[0]?.contractId}
          clientIBAN={clientIban?.data?.iban}
          disableButton={lastPaymentStatus !== "completed"}
          loading={fetchingVIBAN}
          whatsappButtonDisabled={!getPrivilege("can_view_client_contact", lead?.privileges)}
        />
      )}

      {!!showNextPayment && (
        <div>
          <Legend title={t("Add Payment")} />

          <Form.Item
            name="amount"
            label={t("Amount")}
            dependencies={["saleType"]}
            required
            onChange={e => onChangeAmount(e.target.value, minTokenDetails)}
            extra={
              <SuggestedAmountTags suggestedAmounts={suggestedAmounts} onAmountClick={handleSuggestedAmountClick} />
            }
            rules={amountValidationRules}
            initialValue={null}
          >
            <TextInput
              placeholder={t("Enter amount")}
              suffixText={suffixHandler()}
              disabled={!leadSalesInfo?.listingId && (!project || !unit)}
            />
          </Form.Item>

          {!!isRaEnabled && !paymentProof?.fileList?.length && (
            <RACheckbox form={form} fieldName="enableRA" value={enableRA} getAccountant={getAccountant} />
          )}

          {!!enableRA && (
            <AccountantSection
              form={form}
              accountantData={accountantData}
              isLoadingAccountant={isLoadingAccountant}
              platformKey={userPlatformKey}
              projectId={projectId}
            />
          )}

          {!enableRA && (
            <Form.Item
              name="paymentProof"
              label={t("Payment Proof")}
              required
              rules={[
                {
                  validator: (_, value) =>
                    !value || !value?.fileList?.length
                      ? Promise.reject(t("Payment Proof is Required"))
                      : Promise.resolve(),
                },
              ]}
            >
              <PaymentProofImageUpload
                setFiles={handlePaymentProofUpload}
                paymentProofProps={{
                  attachmentTypeId: 1,
                  api: !!ocrEnabled,
                  visionAllowedonProject: !!ocrEnabled,
                  detectPaymentMode: !!ocrEnabled,
                }}
                platformKey={userPlatformKey}
                projectId={projectId}
              />
            </Form.Item>
          )}

          <ExtraFieldsSection
            paymentModesMap={paymentModesMap}
            paymentProof={paymentProof}
            form={form}
            getAccountant={getAccountant}
            accountantData={accountantData}
            isLoadingAccountant={isLoadingAccountant}
            platformKey={userPlatformKey}
            projectId={projectId}
            ocrEnabled={ocrEnabled}
            attachmentType={modeOfPayment || paymentProof?.imageData?.paymentModeKey}
            imageData={ocrData}
            isRaEnabled={isRaEnabled}
          />

          {!enableRA &&
            ((ocrEnabled && paymentProof?.imageData) || (!ocrEnabled && paymentProof?.fileList?.length)) && (
              <PaymentDetailsCollapse
                ocrEnabled={ocrEnabled}
                isLoading={isLoading}
                paymentProof={paymentProof}
                rules={rules}
                depositSlip={depositSlip}
              />
            )}
        </div>
      )}
    </>
  )
}

export default PaymentSection
